import { createFileRoute, Link, useParams } from '@tanstack/react-router'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/use-auth'
import { CTACatalogo, CTALista } from '@/components/blog-components'
import { useQuery } from '@tanstack/react-query'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeSlug from 'rehype-slug'
import rehypeSanitize from 'rehype-sanitize'
const stripFrontmatter = (raw: string) =>
  raw.replace(/^\uFEFF?\s*---\r?\n[\s\S]*?\r?\n---\s*(\r?\n|$)/, '')

const ArticleJSONLD = ({ artigo }: { artigo: any }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": artigo.titulo,
    "description": artigo.meta_description || artigo.resumo,
    "image": artigo.imagem_capa ? [artigo.imagem_capa] : [],
    "datePublished": artigo.publicado_em,
    "dateModified": artigo.updated_at || artigo.publicado_em,
    "author": {
      "@type": "Person",
      "name": artigo.profiles?.nome || "Gopage"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Gopage",
      "logo": {
        "@type": "ImageObject",
        "url": "https://listafornecedores.com.br/logo.png"
      }
    }
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { 
  ChevronRight, 
  Clock, 
  Calendar, 
  Copy, 
  MessageCircle,
  ArrowLeft,
  List,
  Plus,
  Minus,
  HelpCircle,
  ExternalLink
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'
import { useEffect, useState, useMemo } from 'react'
import { Helmet } from 'react-helmet-async'

export const Route = createFileRoute('/guias/$categoria/$slug')({
  component: ArtigoPage,
})

function ArtigoPage() {
  const { slug } = useParams({ from: '/guias/$categoria/$slug' })
  const { profile } = useAuth()
  const [readingProgress, setReadingProgress] = useState(0)
  const [activeHeading, setActiveHeading] = useState<string | null>(null)

  const { data: artigo, isLoading } = useQuery({
    queryKey: ['artigo-blog', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('artigos')
        .select('*, categorias_blog(id, nome, slug), profiles(nome)')
        .eq('slug', slug)
        .eq('status', 'publicado')
        .single()
      
      if (error) throw error
      
      // Increment views
      await supabase.rpc('incrementar_visualizacao_artigo', { artigo_id: data.id })
      
      return data
    },
  })

  const { data: relacionados } = useQuery({
    queryKey: ['artigos-relacionados', artigo?.id, artigo?.categoria_id],
    enabled: !!artigo?.id && !!artigo?.categoria_id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('artigos')
        .select('titulo, slug, imagem_capa, visualizacoes, tempo_leitura, publicado_em, categorias_blog(slug, nome)')
        .eq('categoria_id', artigo!.categoria_id!)
        .eq('status', 'publicado')
        .neq('id', artigo!.id)
        .order('publicado_em', { ascending: false })
        .limit(3)
      
      if (error) throw error
      return data
    }
  })

  // Parse markdown content and frontmatter
  const markdownContent = useMemo(
    () => (artigo?.conteudo ? stripFrontmatter(artigo.conteudo) : ''),
    [artigo?.conteudo]
  )

  const headings = useMemo(() => {
    if (!markdownContent) return []
    const lines = markdownContent.split('\n')
    // Filter H2 headings, skipping H1 as requested
    const h2Lines = lines.filter((line: string) => line.startsWith('## '))
    return h2Lines.map((line: string) => {
      const text = line.replace('## ', '').trim()
      const slug = text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^\w\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-')
      return { text, slug }
    })
  }, [markdownContent])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveHeading(entry.target.id)
          }
        })
      },
      { rootMargin: '-100px 0px -70% 0px' }
    )

    const headingElements = document.querySelectorAll('.prose h2[id]')
    headingElements.forEach((el) => observer.observe(el))

    const updateProgress = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight
      if (scrollHeight > 0) {
        setReadingProgress((window.scrollY / scrollHeight) * 100)
      }
    }
    
    window.addEventListener('scroll', updateProgress)
    return () => {
      window.removeEventListener('scroll', updateProgress)
      observer.disconnect()
    }
  }, [markdownContent])

  const shareWhatsApp = () => {
    const url = window.location.href
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(artigo?.titulo + ' ' + url)}`, '_blank', 'noopener,noreferrer')
  }

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href)
    toast.success('Link copiado!')
  }

  if (isLoading) return <div className="max-w-[720px] mx-auto py-20">Carregando guia...</div>
  if (!artigo) return <div className="max-w-[720px] mx-auto py-20">Guia não encontrado.</div>

  return (
    <article className="pb-20">
      <Helmet>
        <title>{artigo.meta_title || `${artigo.titulo} | Guia Gopage`}</title>
        <meta name="description" content={artigo.meta_description || artigo.resumo} />
        <link rel="canonical" href={`https://listafornecedores.com.br/guias/${artigo.categorias_blog?.slug || 'geral'}/${artigo.slug}`} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={artigo.meta_title || artigo.titulo} />
        <meta property="og:description" content={artigo.meta_description || artigo.resumo} />
        {artigo.imagem_capa && <meta property="og:image" content={artigo.imagem_capa} />}
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={artigo.meta_title || artigo.titulo} />
        <meta name="twitter:description" content={artigo.meta_description || artigo.resumo} />
        {artigo.imagem_capa && <meta name="twitter:image" content={artigo.imagem_capa} />}
      </Helmet>
      
      <ArticleJSONLD artigo={artigo} />

      <div className="fixed top-0 left-0 w-full h-1 z-[60] pointer-events-none">
        <div 
          className="h-full bg-[#C6F764] transition-all duration-150" 
          style={{ width: `${readingProgress}%` }}
        />
      </div>

      <div className="max-w-[1100px] mx-auto px-4 flex flex-col lg:flex-row gap-12 pt-12">
        <aside className="hidden lg:block w-64 shrink-0">
          <div className="sticky top-24">
            <div id="indice-conteudo" className="text-[#0C1A12] font-semibold text-base mb-6 scroll-mt-24">
              Índice
            </div>
            <nav className="flex flex-col gap-[10px]">
              {headings.map((h, i) => (
                <button 
                  key={i} 
                  onClick={() => {
                    const element = document.getElementById(h.slug);
                    if (element) {
                      const offset = 100;
                      const bodyRect = document.body.getBoundingClientRect().top;
                      const elementRect = element.getBoundingClientRect().top;
                      const elementPosition = elementRect - bodyRect;
                      const offsetPosition = elementPosition - offset;
                      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                    }
                  }}
                  className="block w-full text-left text-[15px] leading-relaxed transition-all duration-200"
                  style={activeHeading === h.slug 
                    ? { color: '#0C1A12', fontWeight: 600, borderLeft: '3px solid #C6F764', paddingLeft: '12px', backgroundColor: '#F0F7DC' } 
                    : { color: '#4B5563', paddingLeft: '12px' }
                  }
                >
                  {h.text}
                </button>
              ))}
              {headings.length === 0 && (
                <span className="text-xs text-[#6B7280] italic">Este guia não possui subtítulos.</span>
              )}
            </nav>
          </div>
        </aside>

        <div className="max-w-[720px] flex-grow">
          <nav className="flex items-center gap-2 text-sm text-[#6B7280] mb-8 overflow-x-auto whitespace-nowrap pb-2">
            <Link to="/" className="hover:text-[#0C1A12]">Início</Link>
            <ChevronRight className="w-4 h-4 shrink-0" />
            <Link to="/guias" className="hover:text-[#0C1A12]">Guias</Link>
            <ChevronRight className="w-4 h-4 shrink-0" />
            <Link 
              to="/guias/$categoria" 
              params={{ categoria: artigo.categorias_blog?.slug || 'geral' }}
              className="hover:text-[#0C1A12]"
            >
              {artigo.categorias_blog?.nome || 'Guia'}
            </Link>
            <ChevronRight className="w-4 h-4 shrink-0" />
            <span className="text-[#0C1A12] font-medium truncate">{artigo.titulo}</span>
          </nav>

          <header className="mb-12">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className="inline-block px-3 py-1 bg-[#C6F764]/10 text-[#0C1A12] text-xs font-bold rounded-full">
                {artigo.categorias_blog?.nome || 'Guia'}
              </div>
              {headings.length > 0 && (
                <button 
                  onClick={() => {
                    const element = document.getElementById('indice-conteudo');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="text-xs font-medium text-[#6B7280] hover:text-[#C6F764] transition-colors underline decoration-dotted"
                >
                  Pular para o sumário
                </button>
              )}
            </div>
            <h1 className="text-4xl md:text-[40px] font-bold text-[#1A1A1A] leading-tight mb-6">
              {artigo.titulo}
            </h1>
            
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[#6B7280] text-sm mb-8">
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Atualizado em {artigo.publicado_em && format(new Date(artigo.publicado_em), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {artigo.tempo_leitura} min de leitura
              </span>
            </div>

            {artigo.imagem_capa && (
              <img 
                src={artigo.imagem_capa} 
                alt={artigo.titulo}
                className="w-full aspect-video object-cover rounded-2xl shadow-lg"
              />
            )}
          </header>

          <div className="prose prose-lg max-w-none text-[#1A1A1A] 
            prose-headings:text-[#0C1A12] prose-headings:font-bold
            prose-h2:text-[30px] prose-h2:mt-12 prose-h2:mb-4
            prose-h3:text-[22px] prose-h3:mt-8
            prose-p:text-[18px] prose-p:leading-[1.75] prose-p:mb-5
            prose-strong:font-semibold prose-strong:text-[#0C1A12]
            prose-hr:my-12 prose-hr:border-gray-200
            prose-a:text-[#0C1A12] prose-a:underline hover:prose-a:text-[#C6F764] prose-a:transition-colors
            prose-blockquote:border-l-4 prose-blockquote:border-[#C6F764] prose-blockquote:bg-[#F7F9F5] prose-blockquote:p-5 prose-blockquote:not-italic prose-blockquote:rounded-r-lg
            prose-ul:list-disc prose-ul:pl-6 prose-li:mb-2
            prose-ol:list-decimal prose-ol:pl-6
            prose-img:rounded-2xl
            prose-table:w-full prose-table:border prose-table:border-[#E5E7EB] prose-table:rounded-lg prose-table:overflow-hidden
            prose-thead:bg-[#F7F9F5] prose-thead:text-[#0C1A12] prose-thead:font-bold
            prose-th:p-3 prose-th:border prose-th:border-[#E5E7EB]
            prose-td:p-3 prose-td:border prose-td:border-[#E5E7EB]
            [&_table_tr:nth-child(even)]:bg-[#FAFAFA]
          ">
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeSlug, rehypeSanitize]}
              components={{
                h1: () => null, // Remove H1 from content
                h2: ({ children }) => {
                  const text = Array.isArray(children) 
                    ? children.join('') 
                    : String(children || '');
                  
                  const slug = text
                    .toLowerCase()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, "")
                    .replace(/[^\w\s-]/g, '')
                    .trim()
                    .replace(/\s+/g, '-');
                  
                  return <h2 id={slug} className="scroll-mt-24 text-2xl font-bold mt-12 mb-6 text-[#0C1A12] border-b border-[#E5E7EB] pb-3">{children}</h2>;
                },
                a: ({ href, children, ...props }) => {
                  const isInternal = href?.startsWith('/') || href?.includes('listafornecedores.com.br');
                  const internalHref = href?.includes('listafornecedores.com.br') 
                    ? href.split('listafornecedores.com.br')[1] 
                    : href;

                  if (isInternal && internalHref) {
                    return (
                      <Link 
                        to={internalHref as any} 
                        className="text-[#0C1A12] underline decoration-1 underline-offset-4 hover:decoration-[#C6F764] hover:decoration-2 transition-all"
                      >
                        {children}
                      </Link>
                    );
                  }

                  return (
                    <a 
                      href={href} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="inline-flex items-center text-[#0C1A12] underline decoration-1 underline-offset-4 hover:decoration-[#C6F764] hover:decoration-2 transition-all"
                      {...props}
                    >
                      {children}
                      <ExternalLink className="w-3 h-3 ml-1 text-gray-400" />
                    </a>
                  );
                },
                table: ({ children }) => (
                  <div className="overflow-x-auto my-8 border border-[#E5E7EB] rounded-xl">
                    <table className="min-w-full divide-y divide-[#E5E7EB]">{children}</table>
                  </div>
                ),
                thead: ({ children }) => <thead className="bg-[#F7F9F5]">{children}</thead>,
                th: ({ children }) => <th className="px-4 py-3 text-left text-sm font-bold text-[#0C1A12] uppercase tracking-wider">{children}</th>,
                td: ({ children }) => <td className="px-4 py-3 text-sm text-[#1A1A1A] border-t border-[#E5E7EB]">{children}</td>,
                blockquote: ({ children }) => (
                  <blockquote className="border-l-4 border-[#C6F764] pl-6 py-2 my-8 italic text-[#6B7280] bg-[#F7F9F5] rounded-r-xl">
                    {children}
                  </blockquote>
                )
              }}
            >
              {markdownContent}
            </ReactMarkdown>
          </div>

          {artigo.faq && Array.isArray(artigo.faq) && artigo.faq.length > 0 && (
            <section className="mt-16 pt-12 border-t border-[#E5E7EB]">
              <h2 className="text-3xl font-bold text-[#0C1A12] mb-8 flex items-center gap-3">
                <HelpCircle className="w-8 h-8 text-[#C6F764]" />
                Perguntas Frequentes
              </h2>
              <div className="space-y-4">
                {artigo.faq.map((item: any, idx: number) => (
                  <details 
                    key={idx} 
                    className="group bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden transition-all hover:border-[#C6F764]/50"
                  >
                    <summary className="flex items-center justify-between p-6 cursor-pointer list-none font-bold text-[#1A1A1A] hover:text-[#0C1A12]">
                      {item.pergunta}
                      <div className="w-6 h-6 rounded-full bg-[#F7F9F5] flex items-center justify-center group-open:bg-[#C6F764] transition-colors">
                        <Plus className="w-4 h-4 group-open:hidden" />
                        <Minus className="w-4 h-4 hidden group-open:block text-[#0C1A12]" />
                      </div>
                    </summary>
                    <div className="px-6 pb-6 text-[#6B7280] leading-relaxed">
                      {item.resposta}
                    </div>
                  </details>
                ))}
              </div>
            </section>
          )}

          <div className="mt-14 space-y-6">
            <CTACatalogo slug={slug} />
            <CTALista status={profile?.status} />
          </div>

          <RelacionadosSection relacionados={relacionados} categoria_id={artigo.categoria_id} atual_id={artigo.id} />

          <div className="mt-16 pt-8 border-t border-[#E5E7EB] flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={shareWhatsApp}
                className="rounded-full gap-2 border-[#E5E7EB] hover:bg-[#F7F9F5] h-11 px-5"
              >
                <MessageCircle className="w-4 h-4 text-[#25D366]" />
                WhatsApp
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={copyLink}
                className="rounded-full gap-2 border-[#E5E7EB] hover:bg-[#F7F9F5] h-11 px-5"
              >
                <Copy className="w-4 h-4" />
                Copiar link
              </Button>
            </div>

            <Link to="/guias">
              <Button variant="ghost" size="sm" className="gap-2 text-[#6B7280] hover:text-[#0C1A12]">
                <ArrowLeft className="w-4 h-4" />
                Voltar aos guias
              </Button>
            </Link>
          </div>
          <div className="w-full h-px bg-[#E5E7EB] mt-8" />
        </div>
      </div>
    </article>
  )
}

function RelacionadosSection({ relacionados, categoria_id, atual_id }: { relacionados: any[] | undefined, categoria_id?: string | null, atual_id?: string }) {
  const { data: fallbackRelacionados } = useQuery({
    queryKey: ['artigos-recentes-categoria-fallback', categoria_id, atual_id],
    enabled: (!relacionados || relacionados.length === 0) && !!categoria_id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('artigos')
        .select('titulo, slug, imagem_capa, tempo_leitura, publicado_em, categorias_blog(slug, nome)')
        .eq('categoria_id', categoria_id!)
        .eq('status', 'publicado')
        .neq('id', atual_id!)
        .order('publicado_em', { ascending: false })
        .limit(3)
      
      if (error) throw error
      return data
    }
  })

  const posts = (relacionados && relacionados.length > 0) ? relacionados : (fallbackRelacionados || [])
  
  if (posts.length === 0) return null

  return (
    <section className="mt-16">
      <h2 className="text-[26px] font-bold text-[#0C1A12] mb-8 mt-16">Leia também</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {posts.map((rel) => (
          <Link 
            key={rel.slug} 
            to="/guias/$categoria/$slug"
            params={{ 
              categoria: rel.categorias_blog?.slug || 'geral',
              slug: rel.slug
            }}
            className="group block"
          >
            <div className="aspect-[16/9] rounded-xl overflow-hidden mb-4 border-2 border-transparent group-hover:border-[#C6F764] group-hover:-translate-y-1 transition-all duration-300 shadow-sm group-hover:shadow-md">
              <img 
                src={rel.imagem_capa || '/placeholder.svg'} 
                alt={rel.titulo}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="space-y-2">
              <div className="inline-block px-2 py-0.5 bg-[#C6F764]/10 text-[#0C1A12] text-[10px] font-bold rounded-full uppercase">
                {rel.categorias_blog?.nome || 'Guia'}
              </div>
              <h3 className="text-[#1A1A1A] font-bold text-lg leading-snug group-hover:text-[#0C1A12] line-clamp-2 transition-colors">
                {rel.titulo}
              </h3>
              <div className="flex items-center gap-2 text-[#6B7280] text-xs">
                <Clock className="w-3.5 h-3.5" />
                <span>{rel.tempo_leitura || 5} min de leitura</span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  )
}
